/**
 * Categorizes ChannelMessage ErrorType + ErrorMessage into a user-friendly
 * summary with a suggested action. The raw ErrorMessage is always preserved
 * and surfaced via a "show details" affordance in the UI.
 */

export type ChannelMessageErrorType =
  | 'AgentFailure'
  | 'SendFailure'
  | 'CredentialFailure'
  | 'ProviderNotFound'
  | 'ValidationFailure'
  | 'ChannelConfigFailure'
  | 'Timeout'
  | 'RateLimited'
  | 'Other'
  | string
  | null;

export interface FriendlyError {
  /** Short label shown in the red pill/badge. */
  title: string;
  /** One-sentence human-readable description of what went wrong. */
  summary: string;
  /** Optional next-step guidance for the user. */
  action?: string;
  /** CSS variant for styling (aligns with existing error-type classes). */
  cssClass: string;
  /** Whether this error is likely caused by the AI provider credential (key, tier, quota). */
  isAICredentialIssue: boolean;
  /** Whether this error is a send/delivery problem vs. an AI generation problem. */
  isSendIssue: boolean;
}

const AI_CREDENTIAL_HINT =
  'Check your AI provider credential (key, tier, or usage limits) under Administration → Credentials.';

const SEND_CREDENTIAL_HINT =
  'Check the channel credential (sending mailbox or SMS provider) under Administration → Credentials.';

/**
 * The server (IzzyLib AgentFailureClassifier) tags the first line of ErrorMessage with a stable
 * machine-readable class, e.g. `[MODEL_CONTENT_BLOCKED] The AI model refused…`. The tag is what
 * the UI keys on; the sentence after it is the staff summary. Checked before any substring
 * heuristics so a classified error is never re-guessed from its technical detail.
 */
function detectTaggedCategory(raw: string): FriendlyError | null {
  const m = /^\s*\[([A-Z_]+)\]\s*(.*)$/m.exec(raw);
  if (!m) return null;
  const tag = m[1];
  const serverSummary = (m[2] || '').trim();

  switch (tag) {
    case 'MODEL_CONTENT_BLOCKED':
      return {
        title: 'AI Model Refused',
        summary: serverSummary || 'The AI model’s content filter refused to read this email.',
        action:
          'Reply manually. Force Generate will refuse again until a different AI vendor is configured for this organization.',
        cssClass: 'error-agent',
        isAICredentialIssue: false,
        isSendIssue: false
      };
    case 'VENDOR_AUTH':
      return {
        title: 'No Usable AI Key',
        summary: serverSummary || 'Every AI model that was tried rejected the credential.',
        action: 'Verify the AI vendor key under Administration → Credentials, and that the configured models belong to a vendor this organization holds a key for.',
        cssClass: 'error-credential',
        isAICredentialIssue: true,
        isSendIssue: false
      };
    case 'QUOTA':
      return {
        title: 'Quota / Billing Issue',
        summary: serverSummary || 'The AI provider reports that this key has no remaining quota.',
        action:
          'Top up billing on the provider account, or switch to a credential on a plan with sufficient quota.',
        cssClass: 'error-credential',
        isAICredentialIssue: true,
        isSendIssue: false
      };
    case 'RATE_LIMITED':
      return {
        title: 'Rate Limit Exceeded',
        summary: serverSummary || 'The AI provider throttled the request.',
        action: 'Reprocess the message in a few minutes. If it keeps happening, upgrade the provider tier.',
        cssClass: 'error-rate-limited',
        isAICredentialIssue: true,
        isSendIssue: false
      };
    case 'CONTEXT_LENGTH':
      return {
        title: 'Context Length Exceeded',
        summary: serverSummary || 'The prompt exceeded the model’s context window.',
        action: 'Switch to a larger-context model or reduce knowledge injected into the prompt.',
        cssClass: 'error-agent',
        isAICredentialIssue: false,
        isSendIssue: false
      };
    case 'MODEL_UNAVAILABLE':
      return {
        title: 'Model Not Available',
        summary: serverSummary || 'The API key does not have access to the model this configuration requires.',
        action:
          'Confirm the provider account/tier grants access to the configured model, or pick a different model.',
        cssClass: 'error-credential',
        isAICredentialIssue: true,
        isSendIssue: false
      };
    case 'PIPELINE_STALLED':
      return {
        title: 'AI Pipeline Did Not Converge',
        summary: serverSummary || 'The AI pipeline stopped before producing a reply and no provider error was recorded.',
        action: 'Reprocess once. If it repeats, contact support with the message link.',
        cssClass: 'error-agent',
        isAICredentialIssue: false,
        isSendIssue: false
      };
    case 'AGENT_FAILURE':
      return {
        title: 'AI Generation Failed',
        summary: serverSummary || 'The AI agent could not generate a reply for this message.',
        action: 'Open the details below for the technical cause.',
        cssClass: 'error-agent',
        isAICredentialIssue: false,
        isSendIssue: false
      };
    default:
      return null;
  }
}

/**
 * Inspects the raw error message for well-known substrings returned by
 * OpenAI, Anthropic and Google Gemini. These strings come from the model
 * providers' error payloads, so they are stable enough to pattern-match.
 * Also covers rows written before the server started tagging errors.
 */
function detectAISubCategory(raw: string): FriendlyError | null {
  const s = raw.toLowerCase();

  if (
    s.includes('content blocked by model') ||
    s.includes('recitation') ||
    s.includes('blocked by content filter') ||
    s.includes('content_filter')
  ) {
    return {
      title: 'AI Model Refused',
      summary: 'The AI model’s content filter refused to read this email (for example Google’s RECITATION filter on text that resembles published material).',
      action: 'Reply manually, or Force Generate after a different AI vendor is configured for this organization.',
      cssClass: 'error-agent',
      isAICredentialIssue: false,
      isSendIssue: false
    };
  }

  if (
    s.includes('invalid_api_key') ||
    s.includes('incorrect api key') ||
    s.includes('invalid x-api-key') ||
    s.includes('authentication_error') ||
    s.includes('api key not valid') ||
    s.includes('401')
  ) {
    return {
      title: 'Invalid API Key',
      summary: 'The AI provider rejected the API key.',
      action:
        'Verify the key value is correct and still active in the provider dashboard.',
      cssClass: 'error-credential',
      isAICredentialIssue: true,
      isSendIssue: false
    };
  }

  if (
    s.includes('insufficient_quota') ||
    s.includes('billing') ||
    s.includes('quota_exceeded') ||
    s.includes('exceeded your current quota') ||
    s.includes('payment required') ||
    s.includes('402')
  ) {
    return {
      title: 'Quota / Billing Issue',
      summary: 'The AI provider reports that this key has no remaining quota.',
      action:
        'Top up billing on the provider account, or switch to a credential on a plan with sufficient quota.',
      cssClass: 'error-credential',
      isAICredentialIssue: true,
      isSendIssue: false
    };
  }

  if (
    s.includes('rate limit') ||
    s.includes('rate_limit') ||
    s.includes('tokens per min') ||
    s.includes('tpm') ||
    s.includes('too many requests') ||
    s.includes('429')
  ) {
    return {
      title: 'Rate Limit Exceeded',
      summary:
        'The AI provider throttled the request. Izzy uses 300k–400k tokens per message, which requires a higher provider tier.',
      action:
        'Upgrade the provider tier (OpenAI Tier 3+, Anthropic Tier 2+, Gemini Tier 1+) or reduce the message volume.',
      cssClass: 'error-rate-limited',
      isAICredentialIssue: true,
      isSendIssue: false
    };
  }

  if (
    s.includes('model_not_found') ||
    s.includes('model not found') ||
    s.includes('does not have access to model') ||
    s.includes('model_access_denied') ||
    s.includes('permission_denied') ||
    s.includes('not authorized to access model')
  ) {
    return {
      title: 'Model Not Available',
      summary:
        'The API key does not have access to the model this configuration requires.',
      action:
        'Confirm the provider account/tier grants access to the configured model, or pick a different model.',
      cssClass: 'error-credential',
      isAICredentialIssue: true,
      isSendIssue: false
    };
  }

  if (
    s.includes('context_length_exceeded') ||
    s.includes('context window') ||
    s.includes('maximum context length')
  ) {
    return {
      title: 'Context Length Exceeded',
      summary:
        'The prompt exceeded the model’s context window. This can happen with very long threads or large knowledge payloads.',
      action:
        'Switch to a larger-context model or reduce knowledge injected into the prompt.',
      cssClass: 'error-agent',
      isAICredentialIssue: false,
      isSendIssue: false
    };
  }

  if (
    s.includes('consecutive failed steps') ||
    s.includes('agent terminated') ||
    s.includes('maximum iteration limit') ||
    s.includes('consecutive unproductive retries') ||
    s.includes('final payload validation failed') ||
    s.includes('mandatory_subagent_failed')
  ) {
    return {
      title: 'Agent Gave Up',
      summary:
        'The AI agent retried several times and could not produce a valid reply. The underlying error is usually a provider issue (invalid key, rate limit, or model access).',
      action:
        'Open the details below to see the last underlying error, then check the AI provider credential.',
      cssClass: 'error-agent',
      isAICredentialIssue: true,
      isSendIssue: false
    };
  }

  return null;
}

export function categorizeError(
  errorType: ChannelMessageErrorType,
  errorMessage: string | null | undefined
): FriendlyError {
  const raw = errorMessage ?? '';

  // A server-classified error carries its class on the first line; trust it over heuristics.
  const tagged = detectTaggedCategory(raw);
  if (tagged) return tagged;

  switch (errorType) {
    case 'AgentFailure': {
      const sub = detectAISubCategory(raw);
      if (sub) return sub;
      return {
        title: 'AI Generation Failed',
        summary: 'The AI agent could not generate a reply for this message.',
        action: AI_CREDENTIAL_HINT,
        cssClass: 'error-agent',
        isAICredentialIssue: true,
        isSendIssue: false
      };
    }

    case 'RateLimited': {
      const sub = detectAISubCategory(raw);
      if (sub) return sub;
      return {
        title: 'Rate Limit Exceeded',
        summary:
          'The provider throttled the request. Izzy uses 300k–400k tokens per message and needs a higher tier key.',
        action:
          'Upgrade the provider tier or wait for limits to reset.',
        cssClass: 'error-rate-limited',
        isAICredentialIssue: true,
        isSendIssue: false
      };
    }

    case 'CredentialFailure':
      return {
        title: 'Credential Problem',
        summary:
          'A stored credential could not be decrypted or is missing required values.',
        action: SEND_CREDENTIAL_HINT,
        cssClass: 'error-credential',
        isAICredentialIssue: false,
        isSendIssue: true
      };

    case 'ProviderNotFound':
      return {
        title: 'Provider Not Configured',
        summary:
          'The communication provider for this channel is missing or not registered.',
        action:
          'Open Administration → Channels and confirm the channel type has a valid provider.',
        cssClass: 'error-provider',
        isAICredentialIssue: false,
        isSendIssue: true
      };

    case 'ChannelConfigFailure':
      return {
        title: 'Channel Misconfigured',
        summary:
          'The channel has missing or invalid configuration and cannot be used.',
        action: 'Review the channel setup under Administration → Channels.',
        cssClass: 'error-config',
        isAICredentialIssue: false,
        isSendIssue: true
      };

    case 'ValidationFailure':
      return {
        title: 'Message Incomplete',
        summary:
          'The message was missing required fields (recipient, sender, or content) and could not be sent.',
        cssClass: 'error-validation',
        isAICredentialIssue: false,
        isSendIssue: true
      };

    case 'Timeout':
      return {
        title: 'Timed Out',
        summary: 'The request took too long and was cancelled.',
        action:
          'Try again. If it keeps happening, check the provider status page or credential.',
        cssClass: 'error-timeout',
        isAICredentialIssue: true,
        isSendIssue: false
      };

    case 'SendFailure': {
      // These come from Twilio / MS Graph / other sending providers.
      const s = raw.toLowerCase();
      if (s.includes('401') || s.includes('unauthorized')) {
        return {
          title: 'Send Rejected: Unauthorized',
          summary:
            'The sending provider rejected the credentials used for this channel.',
          action: SEND_CREDENTIAL_HINT,
          cssClass: 'error-send',
          isAICredentialIssue: false,
          isSendIssue: true
        };
      }
      // Size failures first: their message text mentions "recipient mailboxes",
      // which the recipient check below would otherwise misread as an address
      // problem (matches IzzyMSGraphProvider's per-file and total-size guards,
      // plus Graph/Exchange's own size rejections).
      if (
        s.includes('per-file limit') ||
        s.includes('limit for one message') ||
        s.includes('size exceeds') ||
        s.includes('too large')
      ) {
        return {
          title: 'Send Rejected: Attachments Too Large',
          summary: 'The attached files exceed the size limit for one email message.',
          action: 'Re-open the message, remove or shrink the attachments, and approve again.',
          cssClass: 'error-send',
          isAICredentialIssue: false,
          isSendIssue: true
        };
      }
      if (s.includes('mailbox') || s.includes('recipient')) {
        return {
          title: 'Send Rejected: Invalid Recipient',
          summary:
            'The sending provider reported a problem with the recipient address.',
          action: 'Verify the recipient address and try again.',
          cssClass: 'error-send',
          isAICredentialIssue: false,
          isSendIssue: true
        };
      }
      return {
        title: 'Delivery Failed',
        summary: 'The message was approved but the provider failed to send it.',
        action: SEND_CREDENTIAL_HINT,
        cssClass: 'error-send',
        isAICredentialIssue: false,
        isSendIssue: true
      };
    }

    case 'Other':
    default: {
      // Last-ditch pattern match in case ErrorType is missing/unknown
      const sub = detectAISubCategory(raw);
      if (sub) return sub;
      return {
        title: 'Unexpected Error',
        summary: 'Something went wrong and Izzy could not complete this step.',
        action: 'Open the details below for the raw error.',
        cssClass: 'error-other',
        isAICredentialIssue: false,
        isSendIssue: false
      };
    }
  }
}
